import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Tag,
  HelpCircle,
  RefreshCw,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PostsDetail } from "@/types/pocketbase-types";
import { ArticlePost, Outline } from "@/types/article";
import { TElement } from "@udecode/plate";
import { ImagePlugin } from "@udecode/plate-media/react";
import { Badge } from "../ui/badge";

// Interface for link elements
interface TLinkElement extends TElement {
  url?: string;
  href?: string;
}

interface ArticleStatsProps {
  postDetail: PostsDetail;
  post: ArticlePost;
  seoDescription: string;
  keyword: string;
  brandUrl?: string;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
}

export const ArticleStats: React.FC<ArticleStatsProps> = ({
  postDetail,
  post,
  seoDescription,
  keyword,
  brandUrl,
  onRegenerate,
  isRegenerating = false,
}) => {
  const [expandedSections, setExpandedSections] = useState<{
    details: boolean;
    stats: boolean;
  }>({
    details: true,
    stats: true,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  const wordCount = postDetail.content.split(/\s+/).filter(Boolean).length;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));

  // Calculate keyword density
  const calculateKeywordDensity = (
    content: string,
    keyword: string,
  ): string => {
    if (!keyword.trim()) return "0.0%";

    const words = content.toLowerCase().split(/\s+/).filter(Boolean);
    const keywordWords = keyword.toLowerCase().split(/\s+/).filter(Boolean);

    if (keywordWords.length === 0 || words.length === 0) return "0.0%";

    let matches = 0;
    for (let i = 0; i <= words.length - keywordWords.length; i++) {
      let isMatch = true;
      for (let j = 0; j < keywordWords.length; j++) {
        if (words[i + j] !== keywordWords[j]) {
          isMatch = false;
          break;
        }
      }
      if (isMatch) matches++;
    }

    const density = ((matches * keywordWords.length) / words.length) * 100;
    return `${density.toFixed(1)}%`;
  };

  const keywordDensity = calculateKeywordDensity(postDetail.content, keyword);

  // Calculate internal links from both contentState and htmlContent
  const calculateInternalLinks = (
    contentState: TElement[] | null | undefined,
    htmlContent: string,
    brandUrl: string,
  ): number => {
    let internalLinksCount = 0;

    // First, try to count links from contentState (more reliable for real-time updates)
    const countLinksFromContentState = (elements: TElement[]): number => {
      let count = 0;

      elements.forEach((element) => {
        // Check if this is a link element (Plate.js uses LinkPlugin.key which is typically 'a')
        if (element.type === "a" || element.type === "link") {
          const linkElement = element as TLinkElement;
          const href = linkElement.url || linkElement.href;
          // Count ALL links
          if (href) {
            count++;
          }
        }

        // Recursively check children
        if (element.children && Array.isArray(element.children)) {
          count += countLinksFromContentState(element.children as TElement[]);
        }
      });

      return count;
    };

    // Helper function to determine if a link is internal
    const isInternalLink = (href: string, brandUrl: string): boolean => {
      if (!href || !brandUrl) return false;

      try {
        // Check if it's a relative URL (internal)
        if (
          href.startsWith("/") ||
          href.startsWith("./") ||
          href.startsWith("../")
        ) {
          return true;
        }

        // Check if it's an absolute URL pointing to the same domain
        if (href.startsWith("http")) {
          const brandDomain = new URL(brandUrl).hostname.toLowerCase();
          const linkDomain = new URL(href).hostname.toLowerCase();
          return (
            linkDomain === brandDomain ||
            linkDomain === `www.${brandDomain}` ||
            brandDomain === `www.${linkDomain}`
          );
        }
      } catch (e) {
        // Invalid URL
      }

      return false;
    };

    // Count from contentState first (real-time)
    if (contentState && Array.isArray(contentState)) {
      internalLinksCount = countLinksFromContentState(contentState);
    }

    // If no links found in contentState, fallback to HTML parsing
    if (internalLinksCount === 0 && htmlContent && brandUrl) {
      try {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = htmlContent;
        const links = tempDiv.querySelectorAll("a[href]");

        links.forEach((link) => {
          const href = link.getAttribute("href");
          if (href && isInternalLink(href, brandUrl)) {
            internalLinksCount++;
          }
        });
      } catch (e) {
        console.error("Error parsing HTML content for internal links:", e);
      }
    }

    return internalLinksCount;
  };

  const internalLinksCount = calculateInternalLinks(
    postDetail.contentState as TElement[] | null,
    postDetail.htmlContent || "",
    brandUrl || "",
  );

  // Get outline data first
  const outline = postDetail.outline as Outline;
  const headingsCount = outline?.sections?.length || 0;
  const faqCount = outline?.faqs?.length || 0;

  // Calculate images count
  const calculateImagesCount = (
    contentState: TElement[] | null | undefined,
    htmlContent: string,
  ): number => {
    let imagesCount = 0;

    // First, try to count images from contentState (more reliable for real-time updates)
    const countImagesFromContentState = (elements: TElement[]): number => {
      let count = 0;

      elements.forEach((element) => {
        // Check if this is an image element using the correct ImagePlugin key
        if (element.type === ImagePlugin.key) {
          count++;
        }

        // Recursively check children
        if (element.children && Array.isArray(element.children)) {
          count += countImagesFromContentState(element.children as TElement[]);
        }
      });

      return count;
    };

    // Count from contentState first (real-time)
    if (contentState && Array.isArray(contentState)) {
      imagesCount = countImagesFromContentState(contentState);
    }

    // If no images found in contentState, fall back to HTML parsing
    if (imagesCount === 0 && htmlContent) {
      try {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = htmlContent;
        const images = tempDiv.querySelectorAll("img");
        imagesCount = images.length;
      } catch (e) {
        console.error("Error parsing HTML content for images:", e);
      }
      if (post.featuredImage) {
        imagesCount++;
      }
    }

    return imagesCount;
  };

  const imagesCount = calculateImagesCount(
    postDetail.contentState as TElement[] | null,
    postDetail.htmlContent || "",
  );

  // Calculate Flesch Reading Ease Score
  const calculateFleschScore = (content: string): number => {
    if (!content.trim()) return 0;

    const sentences = content
      .split(/[.!?]+/)
      .filter((s) => s.trim().length > 0);
    const words = content.split(/\s+/).filter(Boolean);
    const syllables = words.reduce((total, word) => {
      return total + countSyllables(word);
    }, 0);

    if (sentences.length === 0 || words.length === 0) return 0;

    const avgSentenceLength = words.length / sentences.length;
    const avgSyllablesPerWord = syllables / words.length;

    const fleschScore =
      206.835 - 1.015 * avgSentenceLength - 84.6 * avgSyllablesPerWord;
    return Math.max(0, Math.min(100, fleschScore));
  };

  // Count syllables in a word (simplified algorithm)
  const countSyllables = (word: string): number => {
    word = word.toLowerCase();
    if (word.length <= 3) return 1;

    word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, "");
    word = word.replace(/^y/, "");

    const matches = word.match(/[aeiouy]{1,2}/g);
    return matches ? Math.max(1, matches.length) : 1;
  };

  // Calculate article grade
  const calculateArticleGrade = (): {
    grade: string;
    score: number;
    breakdown: {
      readabilityScore: number;
      seoScore: number;
      fleschScore: number;
      details: {
        wordCount: { score: number; value: number };
        sections: { score: number; value: number };
        faqs: { score: number; value: number };
        readTime: { score: number; value: number };
        flesch: { score: number; value: number };
        keyword: { score: number; value: number };
        links: { score: number; value: number };
        metaTitle: { score: number; value: number };
        metaDesc: { score: number; value: number };
      };
    };
  } => {
    // Readability & Usefulness Score (60% weight)
    let readabilityScore = 0;

    // Word Count scoring
    let wordCountScore = 50;
    if (wordCount >= 1500 && wordCount <= 3000) wordCountScore = 100;
    else if (
      (wordCount >= 1000 && wordCount < 1500) ||
      (wordCount > 3000 && wordCount <= 4000)
    )
      wordCountScore = 80;

    // Sections scoring
    let sectionsScore = 50;
    if (headingsCount >= 5 && headingsCount <= 8) sectionsScore = 100;
    else if (
      (headingsCount >= 3 && headingsCount <= 4) ||
      (headingsCount >= 9 && headingsCount <= 10)
    )
      sectionsScore = 80;

    // FAQs scoring
    let faqScore = 50;
    if (faqCount >= 3 && faqCount <= 7) faqScore = 100;
    else if (
      (faqCount >= 1 && faqCount <= 2) ||
      (faqCount >= 8 && faqCount <= 10)
    )
      faqScore = 80;

    // Read Time scoring
    let readTimeScore = 50;
    if (readTime >= 5 && readTime <= 15) readTimeScore = 100;
    else if (
      (readTime >= 3 && readTime <= 4) ||
      (readTime >= 16 && readTime <= 20)
    )
      readTimeScore = 80;

    // Flesch readability scoring (give full score for now)
    const fleschScore = calculateFleschScore(postDetail.content);
    const fleschScorePoints = 100; // Give full score for readability

    readabilityScore =
      (wordCountScore +
        sectionsScore +
        faqScore +
        readTimeScore +
        fleschScorePoints) /
      5;

    // SEO Ranking Potential Score (40% weight)
    let seoScore = 0;

    // Keyword Density scoring
    const densityValue = parseFloat(keywordDensity.replace("%", ""));
    let keywordScore = 50;
    if (densityValue >= 1.5 && densityValue <= 2.5) keywordScore = 100;
    else if (
      (densityValue >= 1.0 && densityValue < 1.5) ||
      (densityValue > 2.5 && densityValue <= 3.0)
    )
      keywordScore = 80;

    // Links scoring
    let linksScore = 50;
    if (internalLinksCount >= 3) linksScore = 100;
    else if (internalLinksCount === 2) linksScore = 80;
    else if (internalLinksCount === 1) linksScore = 50;

    // Meta Title Length scoring
    const metaTitleLength = post?.seoTitle?.length || 0;
    let metaTitleScore = 50;
    if (metaTitleLength >= 50 && metaTitleLength <= 60) metaTitleScore = 100;
    else if (
      (metaTitleLength >= 40 && metaTitleLength < 50) ||
      (metaTitleLength > 60 && metaTitleLength <= 70)
    )
      metaTitleScore = 80;

    // Meta Description Length scoring
    const metaDescLength = seoDescription.length;
    let metaDescScore = 50;
    if (metaDescLength >= 120 && metaDescLength <= 155) metaDescScore = 100;
    else if (
      (metaDescLength >= 100 && metaDescLength < 120) ||
      (metaDescLength > 155 && metaDescLength <= 170)
    )
      metaDescScore = 80;

    seoScore = (keywordScore + linksScore + metaTitleScore + metaDescScore) / 4;

    // Final weighted score
    const finalScore = readabilityScore * 0.6 + seoScore * 0.4;

    // Grade assignment
    let grade = "F";
    if (finalScore >= 90) grade = "A";
    else if (finalScore >= 80) grade = "B";
    else if (finalScore >= 70) grade = "C";
    else if (finalScore >= 60) grade = "D";

    return {
      grade,
      score: Math.round(finalScore),
      breakdown: {
        readabilityScore: Math.round(readabilityScore),
        seoScore: Math.round(seoScore),
        fleschScore: Math.round(fleschScore),
        details: {
          wordCount: { score: wordCountScore, value: wordCount },
          sections: { score: sectionsScore, value: headingsCount },
          faqs: { score: faqScore, value: faqCount },
          readTime: { score: readTimeScore, value: readTime },
          flesch: { score: fleschScorePoints, value: Math.round(fleschScore) },
          keyword: { score: keywordScore, value: densityValue },
          links: { score: linksScore, value: internalLinksCount },
          metaTitle: { score: metaTitleScore, value: metaTitleLength },
          metaDesc: { score: metaDescScore, value: metaDescLength },
        },
      },
    };
  };

  // Use persisted grade if available, otherwise calculate
  const articleGrade = React.useMemo(() => {
    if (post?.grade && post?.gradeScore !== undefined && post?.gradeBreakdown) {
      // Use persisted grade data
      const breakdown =
        typeof post.gradeBreakdown === "string"
          ? JSON.parse(post.gradeBreakdown)
          : post.gradeBreakdown;

      return {
        grade: post.grade,
        score: post.gradeScore,
        breakdown: breakdown,
      };
    }

    // Fall back to calculating grade
    return calculateArticleGrade();
  }, [
    post?.grade,
    post?.gradeScore,
    post?.gradeBreakdown,
    wordCount,
    headingsCount,
    faqCount,
    readTime,
    keywordDensity,
    internalLinksCount,
    post?.seoTitle,
    seoDescription,
  ]);

  // Format dates
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();

    // Get date parts for comparison (ignoring time)
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffTime = nowOnly.getTime() - dateOnly.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  };

  // Grade color styling
  const getGradeColor = (grade: string) => {
    switch (grade) {
      case "A":
        return "bg-green-100 text-green-600";
      case "B":
        return "bg-blue-100 text-blue-600";
      case "C":
        return "bg-yellow-100 text-yellow-600";
      case "D":
        return "bg-orange-100 text-orange-600";
      case "F":
        return "bg-red-100 text-red-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <div className="w-80 border-l border-gray-200 bg-white overflow-y-auto h-full">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-900">Content Quality</h3>
          <div className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${getGradeColor(articleGrade.grade)}`}
            >
              <span className="font-semibold">{articleGrade.grade}</span>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="p-1 ml-1 text-gray-400 hover:text-gray-600">
                    <HelpCircle size={14} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-sm p-4">
                  <div className="space-y-2 text-sm">
                    <div className="font-medium text-gray-900">
                      Article Grade Formula:
                    </div>
                    <div>
                      <span className="font-medium text-blue-600">
                        Content Quality (60%)
                      </span>
                      <div className="text-gray-600 ml-2">
                        Word count, sections, FAQs, readability
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-green-600">
                        SEO Score (40%)
                      </span>
                      <div className="text-gray-600 ml-2">
                        Keyword density, links, meta tags
                      </div>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      {/* Keyword Section */}
      <div className="px-6 py-2 border-b border-gray-200">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-md font-medium text-gray-700">
            Keyword
          </label>
        </div>
        <Badge variant="secondary">
          <Tag size={14} className="mr-2 text-gray-400"></Tag>
          {keyword}
        </Badge>
      </div>

      {/* Stats Section */}
      <div className="border-b border-gray-200">
        <button
          onClick={() => toggleSection("stats")}
          className="w-full px-6 py-3 flex items-center justify-between text-left text-gray-900 hover:bg-gray-50"
        >
          <span className="font-medium">Stats</span>
          {expandedSections.stats ? (
            <ChevronUp size={16} />
          ) : (
            <ChevronDown size={16} />
          )}
        </button>

        {expandedSections.stats && (
          <div className="px-6 py-3">
            <div className="grid grid-cols-2 gap-3">
              {/* Word Count */}
              <div
                className={`rounded-lg p-3 border ${
                  wordCount >= 1500 && wordCount <= 3000
                    ? "border-green-300"
                    : "border-red-300"
                }`}
              >
                <div className="flex items-center gap-1">
                  <div
                    className={`text-xs font-semibold ${
                      wordCount >= 1500 && wordCount <= 3000
                        ? "text-green-700"
                        : "text-red-700"
                    }`}
                  >
                    Words
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="p-0 text-gray-400 hover:text-gray-600">
                          <HelpCircle size={12} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-sm">
                          Ideal range: 1,500-3,000 words
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center mt-1">
                  <div
                    className={`text-xl font-bold ${
                      wordCount >= 1500 && wordCount <= 3000
                        ? "text-green-800"
                        : "text-red-800"
                    }`}
                  >
                    {wordCount}
                  </div>
                  {wordCount >= 1500 && wordCount <= 3000 && (
                    <span className="ml-2 text-green-600">✅</span>
                  )}
                </div>
                <div
                  className={`text-xs mt-1 ${
                    wordCount >= 1500 && wordCount <= 3000
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {wordCount}/1500
                </div>
              </div>

              {/* Read Time */}
              <div
                className={`rounded-lg p-3 border ${
                  readTime >= 5 && readTime <= 15
                    ? "border-green-300"
                    : "border-red-300"
                }`}
              >
                <div className="flex items-center gap-1">
                  <div
                    className={`text-xs font-semibold ${
                      readTime >= 5 && readTime <= 15
                        ? "text-green-700"
                        : "text-red-700"
                    }`}
                  >
                    Read time
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="p-0 text-gray-400 hover:text-gray-600">
                          <HelpCircle size={12} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-sm">Ideal range: 5-15 minutes</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center mt-1">
                  <div
                    className={`text-xl font-bold ${
                      readTime >= 5 && readTime <= 15
                        ? "text-green-800"
                        : "text-red-800"
                    }`}
                  >
                    {readTime} min
                  </div>
                  {readTime >= 5 && readTime <= 15 && (
                    <span className="ml-2 text-green-600">✅</span>
                  )}
                </div>
                <div
                  className={`text-xs mt-1 ${
                    readTime >= 5 && readTime <= 15
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {readTime}/5 min
                </div>
              </div>

              {/* Sections */}
              <div
                className={`rounded-lg p-3 border ${
                  headingsCount >= 3 && headingsCount <= 5
                    ? "border-green-300"
                    : "border-red-300"
                }`}
              >
                <div className="flex items-center gap-1">
                  <div
                    className={`text-xs font-semibold ${
                      headingsCount >= 3 && headingsCount <= 5
                        ? "text-green-700"
                        : "text-red-700"
                    }`}
                  >
                    Sections
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="p-0 text-gray-400 hover:text-gray-600">
                          <HelpCircle size={12} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-sm">Ideal range: 3-5 sections</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center mt-1">
                  <div
                    className={`text-xl font-bold ${
                      headingsCount >= 3 && headingsCount <= 5
                        ? "text-green-800"
                        : "text-red-800"
                    }`}
                  >
                    {headingsCount}
                  </div>
                  {headingsCount >= 3 && headingsCount <= 5 && (
                    <span className="ml-2 text-green-600">✅</span>
                  )}
                </div>
                <div
                  className={`text-xs mt-1 ${
                    headingsCount >= 3 && headingsCount <= 5
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {headingsCount}/3
                </div>
              </div>

              {/* FAQs */}
              <div
                className={`rounded-lg p-3 border ${
                  faqCount >= 3 && faqCount <= 7
                    ? "border-green-300"
                    : "border-red-300"
                }`}
              >
                <div className="flex items-center gap-1">
                  <div
                    className={`text-xs font-semibold ${
                      faqCount >= 3 && faqCount <= 7
                        ? "text-green-700"
                        : "text-red-700"
                    }`}
                  >
                    FAQs
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="p-0 text-gray-400 hover:text-gray-600">
                          <HelpCircle size={12} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-sm">Ideal range: 3-7 FAQs</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center mt-1">
                  <div
                    className={`text-xl font-bold ${
                      faqCount >= 3 && faqCount <= 7
                        ? "text-green-800"
                        : "text-red-800"
                    }`}
                  >
                    {faqCount}
                  </div>
                  {faqCount >= 3 && faqCount <= 7 && (
                    <span className="ml-2 text-green-600">✅</span>
                  )}
                </div>
                <div
                  className={`text-xs mt-1 ${
                    faqCount >= 3 && faqCount <= 7
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {faqCount}/3
                </div>
              </div>

              {/* Keywords */}
              <div
                className={`rounded-lg p-3 border ${(() => {
                  const densityValue = parseFloat(
                    keywordDensity.replace("%", ""),
                  );
                  return densityValue >= 1.5 && densityValue <= 2.5
                    ? "border-green-300"
                    : "border-red-300";
                })()}`}
              >
                <div className="flex items-center gap-1">
                  <div
                    className={`text-xs font-semibold ${(() => {
                      const densityValue = parseFloat(
                        keywordDensity.replace("%", ""),
                      );
                      return densityValue >= 1.5 && densityValue <= 2.5
                        ? "text-green-700"
                        : "text-red-700";
                    })()}`}
                  >
                    Keywords
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="p-0 text-gray-400 hover:text-gray-600">
                          <HelpCircle size={12} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-sm">Ideal range: 1.5-2.5% density</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center mt-1">
                  <div
                    className={`text-xl font-bold ${(() => {
                      const densityValue = parseFloat(
                        keywordDensity.replace("%", ""),
                      );
                      return densityValue >= 1.5 && densityValue <= 2.5
                        ? "text-green-800"
                        : "text-red-800";
                    })()}`}
                  >
                    {keywordDensity}
                  </div>
                  {(() => {
                    const densityValue = parseFloat(
                      keywordDensity.replace("%", ""),
                    );
                    return (
                      densityValue >= 1.5 &&
                      densityValue <= 2.5 && (
                        <span className="ml-2 text-green-600">✅</span>
                      )
                    );
                  })()}
                </div>
                <div
                  className={`text-xs mt-1 ${(() => {
                    const densityValue = parseFloat(
                      keywordDensity.replace("%", ""),
                    );
                    return densityValue >= 1.5 && densityValue <= 2.5
                      ? "text-green-600"
                      : "text-red-600";
                  })()}`}
                >
                  {keywordDensity}/1.5%
                </div>
              </div>

              {/* Links */}
              <div
                className={`rounded-lg p-3 border ${
                  internalLinksCount >= 3
                    ? "border-green-300"
                    : "border-red-300"
                }`}
              >
                <div className="flex items-center gap-1">
                  <div
                    className={`text-xs font-semibold ${
                      internalLinksCount >= 3
                        ? "text-green-700"
                        : "text-red-700"
                    }`}
                  >
                    Links
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="p-0 text-gray-400 hover:text-gray-600">
                          <HelpCircle size={12} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-sm">Ideal: 3+ links</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center mt-1">
                  <div
                    className={`text-xl font-bold ${
                      internalLinksCount >= 3
                        ? "text-green-800"
                        : "text-red-800"
                    }`}
                  >
                    {internalLinksCount}
                  </div>
                  {internalLinksCount >= 3 && (
                    <span className="ml-2 text-green-600">✅</span>
                  )}
                </div>
                <div
                  className={`text-xs mt-1 ${
                    internalLinksCount >= 3 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {internalLinksCount}/3
                </div>
              </div>

              {/* Images Count */}
              <div
                className={`rounded-lg p-3 border ${
                  imagesCount >= 1 && imagesCount <= 4
                    ? "border-green-300"
                    : "border-red-300"
                }`}
              >
                <div className="flex items-center gap-1">
                  <div
                    className={`text-xs font-semibold ${
                      imagesCount >= 1 && imagesCount <= 4
                        ? "text-green-700"
                        : "text-red-700"
                    }`}
                  >
                    Images
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="p-0 text-gray-400 hover:text-gray-600">
                          <HelpCircle size={12} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-sm">Ideal range: 1-4 images</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center mt-1">
                  <div
                    className={`text-xl font-bold ${
                      imagesCount >= 1 && imagesCount <= 4
                        ? "text-green-800"
                        : "text-red-800"
                    }`}
                  >
                    {imagesCount}
                  </div>
                  {imagesCount >= 1 && imagesCount <= 4 && (
                    <span className="ml-2 text-green-600">✅</span>
                  )}
                </div>
                <div
                  className={`text-xs mt-1 ${
                    imagesCount >= 1 && imagesCount <= 4
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {imagesCount}/1
                </div>
              </div>
            </div>

            {/* <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Readability
              </h4>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full"
                  style={{ width: "80%" }}
                ></div>
              </div>
              <div className="flex justify-between mt-1 text-xs text-gray-500">
                <span>Easy</span>
                <span>Advanced</span>
              </div>
            </div> */}
          </div>
        )}
      </div>

      {/* Details Section */}
      <div className="border-b border-gray-200">
        <button
          onClick={() => toggleSection("details")}
          className="w-full px-6 py-3 flex items-center justify-between text-left text-gray-900 hover:bg-gray-50"
        >
          <span className="font-medium">Details</span>
          {expandedSections.details ? (
            <ChevronUp size={16} />
          ) : (
            <ChevronDown size={16} />
          )}
        </button>

        {expandedSections.details && (
          <div className="px-6 py-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Status</span>
              <span className="text-sm text-gray-900 font-medium">
                {post?.status === "published" ? "Published" : "Draft"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Created</span>
              <span className="text-sm text-gray-900">
                {formatDate(postDetail.created)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Modified</span>
              <span className="text-sm text-gray-900">
                {formatDate(postDetail.updated)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Regenerate Section */}
      {onRegenerate && (
        <div className="px-6 py-4">
          <button
            onClick={onRegenerate}
            disabled={isRegenerating}
            className={`w-full flex items-center justify-center px-4 py-2 rounded-md font-medium transition-colors ${
              isRegenerating
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-blue-50 text-blue-600 hover:bg-blue-100"
            }`}
          >
            <RefreshCw
              size={16}
              className={`mr-2 ${isRegenerating ? "animate-spin" : ""}`}
            />
            {isRegenerating ? "Regenerating..." : "Regenerate article"}
          </button>
        </div>
      )}
    </div>
  );
};
